import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauth, object = user
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await api.get("/teams/me");
      setTeam(data);
    } catch (err) {
      // 404 = solo player or admin (no team); 401 = expired session — both expected
      if (err?.response?.status >= 500) console.error("[auth] fetchTeam failed:", err);
      setTeam(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.team_id) await fetchTeam();
    } catch (err) {
      if (err?.response?.status !== 401) console.error("[auth] refresh failed:", err);
      setUser(false);
      setTeam(null);
    }
  }, [fetchTeam]);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip /me check
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setUser(null);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("[auth] logout request failed (continuing client-side):", err); }
    setUser(false);
    setTeam(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, team, setTeam, refresh, fetchTeam, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
